import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, User, Phone, Mail, Calendar, FileText, Activity, Plus, Edit, 
  ClipboardList, Clock, CheckCircle, AlertCircle, ChevronDown, ChevronUp,
  Trash2, Download, Eye, TrendingUp, ClipboardCheck, Loader2
} from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ==================== STATUS BADGE COMPONENT ====================
const StatusBadge = ({ status }) => {
  const config = {
    draft: { 
      label: '📝 Rascunho', 
      className: 'bg-gray-100 text-gray-700 dark:bg-[#1F2937] dark:text-[#9CA3AF]' 
    },
    in_review: { 
      label: '⏳ Em Revisão', 
      className: 'bg-yellow-100 text-yellow-700 dark:bg-[#78350F] dark:text-[#FBBF24]' 
    },
    approved: { 
      label: '✓ Aprovado', 
      className: 'bg-green-100 text-green-700 dark:bg-[#14532D] dark:text-[#4ADE80]' 
    },
    exported: { 
      label: '✓ Exportado', 
      className: 'bg-green-100 text-green-700 dark:bg-[#14532D] dark:text-[#4ADE80]' 
    }
  };
  const { label, className } = config[status] || config.draft;
  return <Badge className={className}>{label}</Badge>;
};

// ==================== TYPE BADGE COMPONENT ====================
const TypeBadge = ({ type }) => {
  const config = {
    avaliacao: { 
      label: 'Avaliação', 
      className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' 
    },
    evolucao: { 
      label: 'Evolução', 
      className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' 
    }
  };
  const { label, className } = config[type] || config.avaliacao;
  return <Badge className={className}>{label}</Badge>;
};

// ==================== CLINICAL HISTORY SECTION ====================
const ClinicalHistorySection = ({ 
  consultations, 
  patient, 
  onEdit, 
  onDelete, 
  onViewFull,
  loadMore,
  hasMore,
  loading
}) => {
  const [expandedItems, setExpandedItems] = useState({});
  
  // Separate evaluations from evolutions (used only for Section A = Prontuário Base)
  const evaluations = consultations.filter(c => 
    c.consultation_type === 'avaliacao' && ['approved', 'exported'].includes(c.status)
  );
  // Chronological history of ALL consultations (avaliação + evolução, any status)
  const timeline = [...consultations].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const evolutions = timeline;  // Preserve existing variable name used below
  
  const latestEvaluation = evaluations[0];
  
  // Stats
  const totalSessions = consultations.length;
  const approvedDocs = consultations.filter(c => ['approved', 'exported'].includes(c.status)).length;
  const pendingDocs = consultations.filter(c => ['draft', 'in_review'].includes(c.status)).length;
  const lastSession = consultations[0]?.created_at;

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getSummary = (doc) => {
    if (!doc?.clinical_documentation) return 'Sem conteúdo';
    const content = doc.clinical_documentation;
    // Try to get a summary from different possible fields
    const summary = content.session_evolution || 
                    content.main_complaint || 
                    content.session_complaint ||
                    content.student_response ||
                    content.anamnesis?.main_complaint ||
                    'Documentação gerada';
    return typeof summary === 'string' ? summary.substring(0, 150) : 'Documentação estruturada';
  };

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{totalSessions}</p>
            <p className="text-sm text-slate-500 dark:text-[#9BA3BF]">Total de Sessões</p>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{lastSession ? formatDate(lastSession).split(' às')[0] : 'N/A'}</p>
            <p className="text-sm text-slate-500 dark:text-[#9BA3BF]">Última Sessão</p>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600 dark:text-[#4ADE80]">{approvedDocs}</p>
            <p className="text-sm text-slate-500 dark:text-[#9BA3BF]">Docs Aprovados</p>
          </CardContent>
        </Card>
        <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600 dark:text-[#FBBF24]">{pendingDocs}</p>
            <p className="text-sm text-slate-500 dark:text-[#9BA3BF]">Docs Pendentes</p>
          </CardContent>
        </Card>
      </div>

      {/* SECTION A: Base Assessment */}
      <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-[#F0F2FF]">
            <ClipboardCheck className="w-5 h-5 text-blue-500" />
            Prontuário Base (Avaliação Inicial)
          </CardTitle>
          <CardDescription className="text-slate-500 dark:text-[#9BA3BF]">
            Documento base com avaliação completa do paciente
          </CardDescription>
        </CardHeader>
        <CardContent>
          {latestEvaluation ? (
            <div className="border rounded-lg p-4 dark:border-[#2A2E42]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <StatusBadge status={latestEvaluation.status} />
                  <span className="text-sm text-slate-500 dark:text-[#9BA3BF]">
                    {formatDate(latestEvaluation.created_at)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => onViewFull(latestEvaluation)} className="dark:border-[#2A2E42] dark:text-[#C8CEDE]">
                    <Eye className="w-4 h-4 mr-1" /> Ver Completo
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onEdit(latestEvaluation)} className="dark:border-[#2A2E42] dark:text-[#C8CEDE]">
                    <Edit className="w-4 h-4 mr-1" /> Editar
                  </Button>
                </div>
              </div>
              
              {/* Collapsible sections of the evaluation */}
              {latestEvaluation.clinical_documentation && (
                <div className="space-y-2">
                  {Object.entries(latestEvaluation.clinical_documentation).map(([key, value]) => {
                    if (key.startsWith('_') || !value) return null;
                    const isExpanded = expandedItems[`eval-${key}`];
                    const label = {
                      anamnesis: 'Anamnese',
                      physical_exam: 'Exame Físico',
                      functional_diagnosis: 'Diagnóstico Funcional',
                      treatment_plan: 'Plano de Tratamento',
                      patient_guidelines: 'Orientações ao Paciente'
                    }[key] || key;
                    
                    return (
                      <div key={key} className="border rounded-lg dark:border-[#2A2E42]">
                        <button 
                          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-[#22263A] text-slate-900 dark:text-[#F0F2FF]"
                          onClick={() => toggleExpand(`eval-${key}`)}
                        >
                          <span className="font-medium">{label}</span>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {isExpanded && (
                          <div className="p-3 pt-0 text-sm text-slate-600 dark:text-[#9BA3BF]">
                            <pre className="whitespace-pre-wrap font-sans">
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-[#9BA3BF]">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma avaliação inicial registrada</p>
              <p className="text-sm mt-1">Crie uma consulta do tipo "Avaliação" para iniciar o prontuário</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECTION B: Evolution Timeline */}
      <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900 dark:text-[#F0F2FF]">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Histórico de Atendimentos
          </CardTitle>
          <CardDescription className="text-slate-500 dark:text-[#9BA3BF]">
            Todas as consultas em ordem cronológica (mais recente primeiro)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {evolutions.length > 0 ? (
            <div className="space-y-4">
              {evolutions.map((consultation) => (
                <div 
                  key={consultation.id} 
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow dark:border-[#2A2E42] dark:hover:bg-[#22263A]"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {consultation.status === 'approved' || consultation.status === 'exported' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : consultation.status === 'in_review' ? (
                          <Clock className="w-5 h-5 text-yellow-500" />
                        ) : (
                          <FileText className="w-5 h-5 text-slate-400 dark:text-[#5C6480]" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-slate-900 dark:text-[#F0F2FF]">
                            {formatDate(consultation.created_at)}
                          </span>
                          <StatusBadge status={consultation.status} />
                          <TypeBadge type={consultation.consultation_type} />
                          {consultation.template_type && (
                            <Badge variant="outline" className="text-xs dark:border-[#2A2E42] dark:text-[#9BA3BF]">
                              {({ pilates: 'Pilates', pilates_clinico: 'Pilates Clínico', consultorio: 'Fisioterapia Clínica' })[consultation.template_type] || consultation.template_type}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 dark:text-[#9BA3BF]">
                          {getSummary(consultation)}
                          {getSummary(consultation).length >= 150 && '...'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {['draft', 'in_review'].includes(consultation.status) && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => onEdit(consultation)}
                            className="dark:border-[#2A2E42] dark:text-[#C8CEDE]"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-500 hover:text-red-700 dark:border-[#2A2E42]"
                            onClick={() => onDelete(consultation)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => onViewFull(consultation)}
                        className="dark:border-[#2A2E42] dark:text-[#C8CEDE]"
                        data-testid={`timeline-view-full-${consultation.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Ver documentação
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => toggleExpand(consultation.id)}
                        className="dark:border-[#2A2E42] dark:text-[#C8CEDE]"
                      >
                        {expandedItems[consultation.id] ? 'Recolher' : 'Ver resumo'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded content */}
                  {expandedItems[consultation.id] && consultation.clinical_documentation && (
                    <div className="mt-4 pt-4 border-t dark:border-[#2A2E42]">
                      <pre className="whitespace-pre-wrap text-sm bg-gray-50 dark:bg-[#1E2130] p-4 rounded-lg font-sans text-slate-700 dark:text-[#9BA3BF]">
                        {JSON.stringify(consultation.clinical_documentation, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Load More */}
              {hasMore && (
                <div className="text-center pt-4">
                  <Button variant="outline" onClick={loadMore} disabled={loading} className="dark:border-[#2A2E42] dark:text-[#C8CEDE]">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Carregar mais
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-[#9BA3BF]">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma evolução registrada</p>
              <p className="text-sm mt-1">As evoluções de sessão aparecerão aqui</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, consultation: null });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const { isDark } = useTheme();
  
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    phone: '',
    email: '',
    medical_history: ''
  });

  // Show success toast if redirected after saving
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Documento salvo com sucesso!');
      // Remove the query param
      navigate(`/patients/${id}`, { replace: true });
    }
  }, [searchParams, id, navigate]);

  useEffect(() => {
    loadPatientData();
  }, [id]);

  const loadPatientData = async () => {
    try {
      const [patientData, consultationsData] = await Promise.all([
        api.getPatient(id),
        api.getPatientConsultations(id)
      ]);
      setPatient(patientData);
      setConsultations(consultationsData);
      setHasMore(consultationsData.length >= 10);
      setFormData({
        name: patientData.name || '',
        date_of_birth: patientData.date_of_birth || '',
        phone: patientData.phone || '',
        email: patientData.email || '',
        medical_history: patientData.medical_history || ''
      });
    } catch (error) {
      toast.error('Erro ao carregar dados do paciente');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      setLoading(true);
      const nextPage = page + 1;
      const moreData = await api.getPatientConsultations(id, { skip: page * 10 });
      setConsultations(prev => [...prev, ...moreData]);
      setPage(nextPage);
      setHasMore(moreData.length >= 10);
    } catch (error) {
      toast.error('Erro ao carregar mais consultas');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSavePatient = async (e) => {
    e.preventDefault();
    try {
      await api.updatePatient(id, formData);
      toast.success('Dados atualizados com sucesso!');
      setIsEditDialogOpen(false);
      loadPatientData();
    } catch (error) {
      toast.error('Erro ao atualizar dados');
    }
  };

  const handleNewConsultation = () => {
    navigate(`/consultation-flow/${id}`);
  };

  const handleEditConsultation = (consultation) => {
    navigate(`/consultation-flow/${id}?consultation=${consultation.id}`);
  };

  const handleDeleteConsultation = async () => {
    if (!deleteDialog.consultation) return;
    
    try {
      await api.deleteConsultation(deleteDialog.consultation.id);
      toast.success('Documento excluído.');
      setDeleteDialog({ open: false, consultation: null });
      loadPatientData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir documento';
      toast.error(message);
    }
  };

  const handleViewFull = (consultation) => {
    navigate(`/consultations/${consultation.id}`);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (loading && !patient) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!patient) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-gray-600 mb-4">Paciente não encontrado</p>
          <Button onClick={() => navigate('/patients')} variant="outline">
            Voltar para pacientes
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const age = calculateAge(patient.date_of_birth);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="patient-detail-page">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/patients')}
            variant="ghost"
            className="mb-4 dark:text-[#C8CEDE]"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para pacientes
          </Button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                {patient.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-[#F0F2FF]">{patient.name}</h1>
                <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-[#9BA3BF]">
                  {age && <span>{age} anos</span>}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="edit-patient-btn" className="dark:border-[#2A2E42] dark:text-[#C8CEDE]">
                    <Edit className="mr-2 h-4 w-4" />
                    Editar Dados
                  </Button>
                </DialogTrigger>
                <DialogContent className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
                  <DialogHeader>
                    <DialogTitle className="dark:text-[#F0F2FF]">Editar Paciente</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSavePatient} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="dark:text-[#9BA3BF]">Nome completo</Label>
                      <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="dark:bg-[#1E2130] dark:border-[#2A2E42] dark:text-[#F0F2FF]" />
                    </div>
                    <div>
                      <Label htmlFor="date_of_birth" className="dark:text-[#9BA3BF]">Data de nascimento</Label>
                      <Input id="date_of_birth" name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} className="dark:bg-[#1E2130] dark:border-[#2A2E42] dark:text-[#F0F2FF]" />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="dark:text-[#9BA3BF]">Telefone</Label>
                      <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} className="dark:bg-[#1E2130] dark:border-[#2A2E42] dark:text-[#F0F2FF]" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="dark:text-[#9BA3BF]">E-mail</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className="dark:bg-[#1E2130] dark:border-[#2A2E42] dark:text-[#F0F2FF]" />
                    </div>
                    <Button type="submit" className="w-full">Salvar alterações</Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Button onClick={handleNewConsultation} data-testid="new-consultation-btn">
                <Plus className="mr-2 h-4 w-4" />
                Nova Consulta
              </Button>
            </div>
          </div>
        </div>

        {/* Patient Info Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          {patient.phone && (
            <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
              <CardContent className="p-4 flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400 dark:text-[#5C6480]" />
                <div>
                  <p className="text-xs text-slate-500 dark:text-[#9BA3BF]">Telefone</p>
                  <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">{patient.phone}</p>
                </div>
              </CardContent>
            </Card>
          )}
          {patient.email && (
            <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
              <CardContent className="p-4 flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400 dark:text-[#5C6480]" />
                <div>
                  <p className="text-xs text-slate-500 dark:text-[#9BA3BF]">E-mail</p>
                  <p className="font-medium text-sm truncate text-slate-900 dark:text-[#F0F2FF]">{patient.email}</p>
                </div>
              </CardContent>
            </Card>
          )}
          {patient.date_of_birth && (
            <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
              <CardContent className="p-4 flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400 dark:text-[#5C6480]" />
                <div>
                  <p className="text-xs text-slate-500 dark:text-[#9BA3BF]">Nascimento</p>
                  <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">
                    {format(new Date(patient.date_of_birth), 'dd/MM/yyyy')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
            <CardContent className="p-4 flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-400 dark:text-[#5C6480]" />
              <div>
                <p className="text-xs text-slate-500 dark:text-[#9BA3BF]">Consultas</p>
                <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">{consultations.length} registradas</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Info and Clinical History */}
        <Tabs defaultValue="history" className="w-full">
          <TabsList className="mb-4 dark:bg-[#1A1D27]">
            <TabsTrigger value="history" data-testid="tab-history" className="dark:data-[state=active]:text-[#F0F2FF] dark:data-[state=inactive]:text-[#9BA3BF]">
              <Activity className="w-4 h-4 mr-2" />
              Histórico Clínico
            </TabsTrigger>
            <TabsTrigger value="info" data-testid="tab-info" className="dark:data-[state=active]:text-[#F0F2FF] dark:data-[state=inactive]:text-[#9BA3BF]">
              <User className="w-4 h-4 mr-2" />
              Informações
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history">
            <ClinicalHistorySection 
              consultations={consultations}
              patient={patient}
              onEdit={handleEditConsultation}
              onDelete={(c) => setDeleteDialog({ open: true, consultation: c })}
              onViewFull={handleViewFull}
              loadMore={loadMore}
              hasMore={hasMore}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="info">
            <Card className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
              <CardHeader>
                <CardTitle className="text-slate-900 dark:text-[#F0F2FF]">Informações do Paciente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500 dark:text-[#9BA3BF]">Nome Completo</Label>
                    <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">{patient.name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 dark:text-[#9BA3BF]">Data de Nascimento</Label>
                    <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">
                      {patient.date_of_birth 
                        ? format(new Date(patient.date_of_birth), 'dd/MM/yyyy')
                        : 'Não informado'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500 dark:text-[#9BA3BF]">Idade</Label>
                    <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">{age ? `${age} anos` : 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 dark:text-[#9BA3BF]">Telefone</Label>
                    <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">{patient.phone || 'Não informado'}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 dark:text-[#9BA3BF]">E-mail</Label>
                    <p className="font-medium text-slate-900 dark:text-[#F0F2FF]">{patient.email || 'Não informado'}</p>
                  </div>
                </div>
                {patient.medical_history && (
                  <div>
                    <Label className="text-slate-500 dark:text-[#9BA3BF]">Histórico Médico</Label>
                    <p className="mt-1 p-3 bg-gray-50 dark:bg-[#1E2130] rounded-lg text-slate-700 dark:text-[#9BA3BF]">
                      {patient.medical_history}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, consultation: deleteDialog.consultation })}>
          <DialogContent className="dark:bg-[#1A1D27] dark:border-[#2A2E42]">
            <DialogHeader>
              <DialogTitle className="dark:text-[#F0F2FF]">Excluir rascunho</DialogTitle>
              <DialogDescription className="dark:text-[#9BA3BF]">
                Tem certeza? Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, consultation: null })} className="dark:border-[#2A2E42] dark:text-[#C8CEDE]" data-testid="confirm-delete-cancel">
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeleteConsultation} data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
